import React, { useState, useMemo, useEffect, useRef } from 'react';
import Loader from "react-loader-spinner";
import { FiSearch, FiCheck, FiPlus } from "react-icons/fi";
import { BASE_URL } from '../constants/config';
import defaultTeamLogo from "../images/defaultTeamLogo.png";
import '../styles/leagueManagement.css';

// У клиента может быть больше тысячи команд, поэтому в DOM держим только
// PAGE_SIZE строк и доливаем по кнопке. Поиск с задержкой, чтобы не
// пересчитывать список на каждое нажатие клавиши.
const PAGE_SIZE = 40;
const SEARCH_DELAY_MS = 250;

const resolveImage = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return BASE_URL.url + image;
};

export default function TeamPicker({
    allTeams, loading, isPicked, onPick, pickedLabel,
}) {
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [visible, setVisible] = useState(PAGE_SIZE);
    const timerRef = useRef(null);

    useEffect(() => {
        timerRef.current = setTimeout(() => setDebounced(search), SEARCH_DELAY_MS);
        return () => clearTimeout(timerRef.current);
    }, [search]);

    // Новый запрос - снова показываем первую страницу
    useEffect(() => { setVisible(PAGE_SIZE); }, [debounced]);

    const filtered = useMemo(() => {
        const query = debounced.trim().toLowerCase();
        if (!query) return allTeams;
        return allTeams.filter(t => t.searchName.includes(query));
    }, [allTeams, debounced]);

    const shown = useMemo(() => filtered.slice(0, visible), [filtered, visible]);
    const searching = search !== debounced;

    return (
        <div className="teamPickerPanel">
            <div className="searchRow">
                <FiSearch className="searchIcon" />
                <input
                    type="text"
                    className="modalInput"
                    value={search}
                    placeholder="Поиск по названию команды"
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                />
            </div>

            {loading ? (
                <div className="teamsLoader">
                    <Loader type="Oval" color="#667eea" height={32} width={32} />
                </div>
            ) : (
                <>
                    <div className="teamPickerList">
                        {shown.length === 0 ? (
                            <p className="teamsEmpty">
                                {searching ? 'Ищем…' : 'Команды не найдены'}
                            </p>
                        ) : shown.map(team => {
                            const picked = isPicked(team.team_id);
                            return (
                                <button
                                    type="button"
                                    key={team.team_id}
                                    className={`teamPickerRow ${picked ? 'teamPickerRowPicked' : ''}`}
                                    onClick={() => !picked && onPick(team)}
                                    disabled={picked}
                                >
                                    <img
                                        src={resolveImage(team.image) || defaultTeamLogo}
                                        alt={team.name}
                                        loading="lazy"
                                        onError={(e) => { e.target.src = defaultTeamLogo; }}
                                    />
                                    <div className="teamPickerText">
                                        <span className="teamChipName">{team.name}</span>
                                    </div>
                                    <span className="teamPickerAction">
                                        {picked ? (pickedLabel || <FiCheck />) : <FiPlus />}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {filtered.length > 0 && (
                        <div className="teamPickerFooter">
                            <span className="fieldHint">
                                Показано {shown.length} из {filtered.length}
                            </span>
                            {shown.length < filtered.length && (
                                <button
                                    type="button"
                                    className="linkButton"
                                    onClick={() => setVisible(v => v + PAGE_SIZE)}
                                >
                                    Показать ещё
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
